import classNames from 'classnames';
import { useRef } from 'react';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import { SearchBar } from '@/components/organisms/search-bar/search-bar';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import styles from './minimal-header.module.scss';

export interface MinimalHeaderProps extends ModuleWrapperProps {
  /** The text to show as a headline */
  headline: Content.Components['MinimalHeaderModuleDto']['headline'];
  /** The image to show */
  image?: Content.Components['MinimalHeaderModuleDto']['image'];
  /** The text to show as a pre headline */
  preHeadline?: Content.Components['MinimalHeaderModuleDto']['preHeadline'];
  /** Search settings for the search bar */
  searchSettings: Content.Components['MinimalHeaderModuleDto']['searchSettings'];
  /** Indicates whether to show the search bar */
  showSearch?: Content.Components['MinimalHeaderModuleDto']['showSearch'];
  /** The text to show as a sub headline */
  subHeadline?: Content.Components['MinimalHeaderModuleDto']['subHeadline'];
}

const MinimalHeader = ({
  headline,
  image,
  preHeadline,
  searchSettings,
  showSearch = false,
  subHeadline,
  ...rest
}: MinimalHeaderProps) => {
  const moduleRef = useRef<HTMLDivElement>(null);

  const propDefaultValues =
    searchSettings && searchSettings.$type === 'SearchSettingsDto'
      ? {
          adultsNumber:
            searchSettings.selectedPaxDistribution?.adultsNo.toString(),
          departureDate: searchSettings.selectedDepartureDate
            ? new Date(searchSettings.selectedDepartureDate.toString())
            : undefined,
          departureId: searchSettings.selectedDepartureId?.toString(),
          destinationIds: searchSettings.selectedDestinationIds?.map(id =>
            id.toString(),
          ),
          destinationRegionIds:
            searchSettings.selectedDestinationRegionIds?.map(id =>
              id.toString(),
            ),
          durationType: searchSettings.selectedPeriodType,
        }
      : undefined;

  const basicSearchSettings =
    searchSettings as Content.Components['SearchSettingsDto'];

  const travelPortalSearchSettings =
    searchSettings && searchSettings.$type === 'TravelPortalSearchSettingsDto'
      ? (searchSettings as Content.Components['TravelPortalSearchSettingsDto'])
      : undefined;

  return (
    <ModuleWrapper
      {...rest}
      ref={moduleRef}
      allowOverflow
      className={classNames(styles.component, {
        [styles.hasSearchBar]: showSearch,
      })}
      hasSearchBar
      padding="none"
    >
      <div className={styles.wrapper}>
        <Container>
          <Row>
            <Column width={{ desktop: 7, laptop: 8 }}>
              <div className={styles.contentWrapper}>
                <div className={styles.headlineWrapper}>
                  {preHeadline && (
                    <Typography
                      className={styles.preHeadline}
                      tag="label"
                      tagStyle="labelMedium"
                    >
                      {preHeadline}
                    </Typography>
                  )}

                  <Typography
                    className={styles.headline}
                    tag="h1"
                    tagStyle="displayMedium"
                  >
                    {headline}
                  </Typography>
                </div>

                {subHeadline && (
                  <Typography
                    className={styles.subHeadline}
                    tag="h2"
                    tagStyle="bodyLargeBold"
                  >
                    {subHeadline}
                  </Typography>
                )}
              </div>
            </Column>

            {image && (
              <Column
                className={styles.maskImageWrapper}
                width={{ desktop: 5, laptop: 4 }}
              >
                <div
                  className={styles.mask}
                  style={{ maskImage: `url(/images/headerCarousel-mask.svg)` }}
                >
                  <Image
                    alt={image.altText}
                    className={styles.maskImage}
                    fill
                    focalPoint={image.focalPoint}
                    height={552}
                    priority
                    src={image.url}
                    srcSet={{
                      desktop: {
                        height: 752,
                        width: 1440,
                      },
                      laptop: {
                        height: 620,
                        width: 1024,
                      },
                      tablet: {
                        height: 552,
                        width: 769,
                      },
                    }}
                    unoptimized
                    width={375}
                  />
                </div>
              </Column>
            )}
          </Row>

          {searchSettings && showSearch && (
            <Row className={styles.searchBarWrapper}>
              <Column>
                <SearchBar
                  compact
                  propDefaultValues={propDefaultValues}
                  searchSettings={basicSearchSettings}
                  shouldNavigate
                  travelPortalSearchSettings={travelPortalSearchSettings}
                />
              </Column>
            </Row>
          )}
        </Container>
      </div>
    </ModuleWrapper>
  );
};

MinimalHeader.displayName = 'MinimalHeader';

export default MinimalHeader;
